/**
 * Lightweight markdown renderer for S.T.E.W.A.R.D. chat messages.
 *
 * Supports: headings, bold, italic, code (inline + block), lists (ordered + unordered),
 * tables, blockquotes, thematic breaks, links, and line breaks. Output is sanitized
 * via DOMPurify.
 *
 * ~5kB contribution to bundle (excluding DOMPurify).
 */
import DOMPurify from 'dompurify';

// A fence opened at a line's start takes any label without a backtick, as
// CommonMark's does (`c++`, `shell script`, ` python`); one opened mid-line
// takes a bare word, so prose that mentions ``` with more text after it on
// its line does not swallow the fence below. A mention that ends its line
// still opens a fence here, which the next ``` closes.
const CLOSED_FENCE = /(^|\n)```([^`\n]*)\n([\s\S]*?)```|```(\w*)\n([\s\S]*?)```/g;

/**
 * A fence label's first word: its language. Words part at a space or tab
 * only, and a CR ends one, exactly as the chat route reads the same label
 * (`src/services/chat.py`): a wider notion of space differs between the two
 * languages, and it decides whether a fence is the answer's own markdown.
 */
const fenceLanguage = (label: string): string => /^[ \t]*([^ \t\r]*)/.exec(label)![1];

/** The fence an ended answer never closes, as both of the widget's renders read it. */
interface UnclosedFence {
  /** Offset of its opening ```, at a line's start. */
  at: number;
  label: string;
  body: string;
}

/**
 * The fence the text never closes, or `null`.
 *
 * The widget reads an answer two ways: whole (this render, for a reloaded
 * conversation) and block by block (`segmentBlocks`, for a live one, where a
 * line starting ``` opens a fence and the next line holding ``` closes it).
 * The two pair backticks differently, since this render also opens mid-line.
 * The fence that runs to the end is the live reveal's: the line walk ends
 * inside a fence opened at P, and P's line holds no backtick past its opener.
 * No line after P holds ```, so the only fence of this render's own that
 * could hold P's ``` is one closing AT P, opened mid-line or at a line's
 * start one step behind the line walk. A labelled P opens the fence the live
 * reveal opened, since a closing fence carries no label, and the text before
 * P is rendered without it. A bare P that closes such a fence stays its
 * close, as a reload has always read it; the live reveal, which never sees an
 * opener mid-line, reads that line apart. The chat route reads an answer the
 * same way (`_reconcile_chart_placeholders`).
 */
function unclosedFence(text: string): UnclosedFence | null {
  let offset = 0;
  let openedAt = -1;
  for (const line of text.split('\n')) {
    if (openedAt !== -1) {
      if (line.includes('```')) openedAt = -1;
    } else if (line.startsWith('```')) {
      openedAt = offset;
    }
    offset += line.length + 1;
  }
  if (openedAt === -1) return null;
  const found = /^```([^`\n]*)(?:\n([\s\S]*))?$/.exec(text.slice(openedAt));
  if (found === null) return null;
  if (
    fenceLanguage(found[1]) === '' &&
    [...text.matchAll(CLOSED_FENCE)].some((m) => m.index + m[0].length - 3 === openedAt)
  ) {
    return null;
  }
  return { at: openedAt, label: found[1], body: found[2] ?? '' };
}

/**
 * Render markdown string to sanitized HTML.
 *
 * `streaming=true` tightens the GFM table body regex so a partial row
 * without a final newline (e.g. mid-stream chunk like `| Bob`) does NOT
 * close the table early and flicker as literal text below it, and leaves a
 * fence the text never closes as written, since its close may be on the
 * way. Default `false` preserves end-of-message behavior for non-streaming
 * callers.
 */
export function renderMarkdown(text: string, streaming = false): string {
  if (!text) return '';

  // Extract code blocks + spans into placeholders BEFORE the remaining
  // markdown passes. Two reasons:
  //   1. Backslash escapes (\* etc.) must NOT apply inside code per
  //      CommonMark — the user wrote `\*` in code expecting a literal
  //      backslash + asterisk.
  //   2. Bold/italic/table regexes must NOT match content inside code,
  //      otherwise rendered code samples lose their formatting.
  // ```markdown / ```md is a special case: in a chat the LLM emitting
  // that fence is signalling "render this as markdown" rather than
  // "show me literal markdown source", so its body is read as the answer's
  // own text, in place, instead of preserved as a code block.
  type Placeholder =
    | { type: 'block'; lang: string; content: string }
    | { type: 'span'; content: string }
    | { type: 'literal'; content: string };
  const placeholders: Placeholder[] = [];
  const tokenize = (ph: Placeholder) => ` C${placeholders.push(ph) - 1} `;

  // A markdown fence's markers are dropped and its body stays where it
  // stood, so its quotes, lists and paragraphs are the answer's own blocks,
  // drawn as the same text without the fence would be when its closing fence
  // ends its line. Its last line break
  // went with the closing fence. Text before a mid-line opener stays; when
  // the body's first line repeats it, as a quote's marker continues onto the
  // fence's lines, it is read once. Its code spans pair inside it, and a
  // backtick left unpaired stays one, as when the body is revealed as its own
  // block: a code span paired across the fence's edge would hide whatever
  // lies between, a chart placeholder included.
  const fenced = (label: string, body: string, before = ''): string => {
    const lang = fenceLanguage(label);
    if (lang !== 'markdown' && lang !== 'md') {
      return tokenize({ type: 'block', lang, content: body });
    }
    const own = before !== '' && body.startsWith(before) ? body.slice(before.length) : body;
    return own
      .replace(/\n$/, '')
      .replace(/`([^`]+)`/g, (_match, code: string) => tokenize({ type: 'span', content: code }))
      .replace(/`/g, (tick: string) => tokenize({ type: 'literal', content: tick }));
  };
  // A fence the answer never closes runs to its end, as CommonMark reads it,
  // so an answer cut off inside a code sample shows the sample as code and
  // never its backticks. See `unclosedFence` for which fence that is.
  const unclosed = streaming ? null : unclosedFence(text);
  let pre = (unclosed === null ? text : text.slice(0, unclosed.at)).replace(
    CLOSED_FENCE,
    (_match, lead: string, label: string | undefined, body: string, word: string,
      wordBody: string, offset: number, whole: string) => {
      if (label !== undefined) return lead + fenced(label, body);
      const lineStart = whole.lastIndexOf('\n', offset - 1) + 1;
      return fenced(word, wordBody, whole.slice(lineStart, offset));
    },
  );
  if (unclosed !== null) pre += fenced(unclosed.label, unclosed.body);

  pre = pre.replace(/`([^`]+)`/g, (_match, body: string) =>
    tokenize({ type: 'span', content: body }),
  );

  let html = escapeHtml(pre);

  // CommonMark backslash escapes: `\X` → literal X for X in the ASCII-
  // punctuation set. Emit the literal as a numeric HTML entity so
  // subsequent regex passes (bold/italic/list/link/etc.) don't see it
  // as a formatter character. Excludes & < > " because escapeHtml has
  // already entity-encoded those four — `\<` after escapeHtml is
  // `\&lt;`, which would not match `\X` here anyway.
  html = html.replace(
    /\\([!#$%'()*+,\-./:;=?@[\]^_`{|}~\\])/g,
    (_m, c: string) => `&#${c.charCodeAt(0)};`,
  );

  // Blockquotes. A run of lines each beginning `> `, or a lone `>`, becomes
  // ONE quote. The `>` is already `&gt;` here, so the escape-first posture
  // holds: a quote's text was escaped like any other. Its lines are joined
  // by breaks onto ONE line, because every block pass below anchors on a
  // line's start: a quoted `- item` or `# title` stays the quote's own text,
  // and only the inline passes (emphasis, code spans, links, citations,
  // chart placeholders) reach inside it.
  html = html.replace(/^&gt;(?: .*)?$(?:\n&gt;(?: .*)?$)*/gm, (run: string) => {
    const lines = run.split('\n').map((line) => line.replace(/^&gt; ?/, ''));
    return `<blockquote>${lines.join('<br>')}</blockquote>`;
  });

  // Thematic break: a line of three or more `-`, `*` or `_` and nothing
  // else. BEFORE the emphasis passes, which would otherwise read `***` and
  // `___` as an emphasised `*` or `_`. A `---` under a line of text is a
  // rule, not a heading: this renderer has no setext headings.
  html = html.replace(/^(?:-{3,}|\*{3,}|_{3,})[ \t]*$/gm, '<hr>');

  // GFM pipe tables — must run AFTER code (so cells contain pre-rendered <code>
  // spans) and BEFORE the paragraph-wrapping step at the end (which converts
  // \n into <br> and would break multi-line table matching).
  // Requires header + separator + at least one body row; partial tables (mid
  // stream) fall through and render as literal text until the body arrives.
  //
  // Body row shape:
  //   - `\|[^\n]+\|?` — leading pipe required, content required, trailing
  //     pipe OPTIONAL so a row like `| 1 | 2` (no closing pipe) still
  //     matches. splitRow handles the no-trailing-pipe shape. (T-38)
  //   - Each non-final row terminates in `\n` and may absorb ONE additional
  //     blank line (`\n\n`). Tolerates a single spurious blank line between
  //     rows introduced by streaming-chunk boundaries while still letting
  //     two blank lines (`\n\n\n`) end the table at a paragraph break — the
  //     row alternative cannot match a leading-`\n` position. (T-36)
  //   - The final body row's trailing `\n` is optional in non-streaming
  //     mode (so an end-of-message table without trailing newline still
  //     renders) but REQUIRED in streaming mode so a dangling partial row
  //     mid-chunk doesn't flicker the table closed and reopen on the next
  //     render pass. (T-37)
  const tableBody = streaming
    ? '((?:\\|[^\\n]+\\|?\\n\\n?)+)'
    : '((?:\\|[^\\n]+\\|?\\n\\n?)*\\|[^\\n]+\\|?\\n?)';
  const tableRe = new RegExp(
    '^\\|(.+)\\|[ \\t]*\\n\\|((?:[ \\t]*:?-+:?[ \\t]*\\|)+)[ \\t]*\\n' + tableBody,
    'gm',
  );
  html = html.replace(
    tableRe,
    (_match, headerLine: string, sepLine: string, bodyBlock: string) => {
      const headers = splitRow(headerLine);
      const aligns = splitRow(sepLine).map(alignFor);
      const bodyRows = bodyBlock
        .split('\n')
        .filter((line) => line.trim().length > 0)
        .map((line) => splitRow(line.replace(/^\|/, '').replace(/\|$/, '')))
        // GFM: excess cells past the header column count are ignored.
        // Without this, an over-cell row emits a stray <td> with empty
        // data-label and no alignment class. (T-39)
        .map((row) => row.slice(0, headers.length));

      const thead =
        '<thead><tr>' +
        headers
          .map((cell, i) => {
            const cls = aligns[i] ? ` class="${aligns[i]}"` : '';
            // scope="col" is the canonical a11y signal that this <th>
            // labels a column; screen readers announce body cells with
            // their associated header. (T-46)
            return `<th${cls} scope="col">${cell}</th>`;
          })
          .join('') +
        '</tr></thead>';

      const tbody =
        '<tbody>' +
        bodyRows
          .map(
            (row) =>
              '<tr>' +
              row
                .map((cell, i) => {
                  const cls = aligns[i] ? ` class="${aligns[i]}"` : '';
                  // data-label carries the column header for card-stacked
                  // mode (CSS uses ::before { content: attr(data-label) }).
                  // Build the attribute value via attributeSafeLabel(): the
                  // input was content-escaped at the top of renderMarkdown
                  // (turning literal " < > & into &quot; &lt; &gt; &amp;),
                  // but HTML attribute parsing decodes those entity refs
                  // back to raw chars in the attribute value. If the
                  // decoded value contained script-like substrings (e.g.
                  // a header was crafted as a"><script>...), DOMPurify
                  // would treat the attribute as a sanitizer-bypass attempt
                  // and silently drop it. attributeSafeLabel() decodes
                  // back to raw text, strips < and > (presentation hint
                  // only — not authoritative content), then re-encodes
                  // for attribute context.
                  const dataLabel = ` data-label="${attributeSafeLabel(headers[i] ?? '')}"`;
                  return `<td${cls}${dataLabel}>${cell}</td>`;
                })
                .join('') +
              '</tr>',
          )
          .join('') +
        '</tbody>';

      return `<div class="table-wrap"><table>${thead}${tbody}</table></div>`;
    },
  );

  // Headings (### before ## before #)
  html = html.replace(/^### (.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^## (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^# (.+)$/gm, '<h2>$1</h2>');

  // Bold (**text** or __text__)
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

  // Italic (*text* or _text_) — but not inside words with underscores
  html = html.replace(/(?<!\w)\*(.+?)\*(?!\w)/g, '<em>$1</em>');
  html = html.replace(/(?<!\w)_(.+?)_(?!\w)/g, '<em>$1</em>');

  // Ordered lists — convert to <oli> placeholder first (before UL processing)
  // so unordered list wrapping doesn't interfere with numbered items.
  html = html.replace(/^(\d+)\. (.+)$/gm, (_match, num, content) => {
    return `<oli value="${num}">${content}</oli>`;
  });

  // Unordered lists
  html = html.replace(/^[\-\*] (.+)$/gm, '<li>$1</li>');
  html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>');

  // Wrap consecutive <oli> runs into <ol>, then rename to <li>.
  // If all items in a group use value="1" (lazy numbering), drop the value
  // attributes so the browser auto-increments (CommonMark behavior).
  html = html.replace(
    /((?:<oli value="\d+">[^]*?<\/oli>\n?)+)/g,
    (_match, group: string) => {
      const allOnes = !/<oli value="(?!1")[^"]*"/.test(group);
      let inner = group.replace(/<\/?oli/g, (t: string) => t.replace('oli', 'li'));
      if (allOnes) {
        inner = inner.replace(/ value="1"/g, '');
      }
      return '<ol>' + inner + '</ol>';
    },
  );

  // Chart placeholders: [chart:<id>] → placeholder div (resolved by steward-message)
  html = html.replace(
    /\[chart:([a-f0-9]+)\]/gi,
    (_match, id) => `<div class="steward-chart-placeholder" data-artifact-id="${id}"></div>`
  );

  // Strip markdown image syntax — images are delivered via artifact metadata, not URLs
  html = html.replace(
    /!\[([^\]]*)\]\(([^)]+)\)/g,
    (_match, alt) => alt
  );

  // Links [text](url) — only allow safe URL schemes
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_match, text, url) => {
      const trimmedUrl = url.trim();
      if (/^https?:\/\//.test(trimmedUrl) || trimmedUrl.startsWith('/')) {
        return `<a href="${trimmedUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`;
      }
      return text; // Strip unsafe links, keep the text
    }
  );

  // Inline citations: [Source: ...] → subtle styled span
  html = html.replace(
    /\[Source:\s*([^\]]+)\]/g,
    '<span class="citation">$1</span>'
  );

  // Line breaks: double newline → paragraph break, single → <br>
  html = html.replace(/\n{2,}/g, '</p><p>');
  html = html.replace(/\n/g, '<br>');

  // Wrap in a paragraph, so text sits in one whatever it opens with: bold, a
  // link, or a block the lift then moves out (a heading, quote, rule, list or
  // table), after which the rest lands in a paragraph, as when the block is
  // revealed on its own, rather than loose after a stray `<br>`. Not text
  // opening with a chart: its placeholder is block-level and stays where it
  // stands, so a paragraph around it would be cut short there.
  if (!html.startsWith('<div class="steward-chart-placeholder"')) {
    html = `<p>${html}</p>`;
  }

  // Restore code-block / code-span / literal-backtick
  // placeholders. Done LAST so DOMPurify sees the final HTML and so no
  // earlier pass can corrupt the restored content.
  html = html.replace(/\x00C(\d+)\x00/g, (_m, idx: string) => {
    const ph = placeholders[+idx];
    if (ph.type === 'block') {
      const langClass = ph.lang ? ` class="language-${escapeHtml(ph.lang)}"` : '';
      // Neutralize [chart:...] inside code blocks so the chart-placeholder
      // pass — which has already run by this point but leaves untouched
      // text intact — is preempted on a future re-render. The original
      // pre-refactor parser had this same neutralization; preserved here
      // because chart placeholders inside code samples (e.g. an LLM
      // showing how to embed a chart) shouldn't be resolved.
      const safeCode = escapeHtml(ph.content.trim()).replace(/\[chart:/g, '&#91;chart:');
      return `<pre><code${langClass}>${safeCode}</code></pre>`;
    }
    if (ph.type === 'span') {
      return `<code>${escapeHtml(ph.content)}</code>`;
    }
    return escapeHtml(ph.content);
  });

  // Lift block-level elements OUT of the paragraphs they landed in.
  //
  // The paragraph pass above turns blank lines into `</p><p>` without
  // knowing what sits between them, and placeholders are restored after
  // it, so a fenced block or a table arrives inside a paragraph:
  // `<p><pre>…</pre></p>`. HTML forbids that — `<p>` is auto-closed by
  // any block-level start tag — so the browser silently ends the
  // paragraph early and leaves the orphaned `</p>` as an EMPTY one.
  // Every code block and table in an answer therefore carried a stray
  // vertical gap that nothing in the CSS explained.
  //
  // The shells dropped here are ONLY the ones this lift empties. An
  // empty `<p>` that arrived some other way is left alone: the fix's
  // blast radius is exactly the defect's.
  html = liftBlocksOutOfParagraphs(html);

  // Same root cause, different victim: the newline pass runs AFTER the
  // list is built, so the newline between `</li>` and `<li>` becomes a
  // `<br>` sitting as a direct child of the `<ul>`. That is invalid —
  // a list's only children are list items — and it renders as extra
  // space between every pair of items in every list the widget draws.
  // Breaks INSIDE an item are untouched, and that holds BY CONSTRUCTION
  // rather than by test: the pattern anchors on a closing `</li>`, so it
  // cannot reach inside an item. (There is no markdown here that puts a
  // break inside an `<li>` anyway — a literal <br> in the source is
  // escaped as text — so a test for it would assert on an unreachable
  // input.)
  //
  // ONE rule, not two. A `<br><\/ul>` cleanup also lived here until
  // mutation testing showed nothing could fail without it: every break
  // inside a list follows a `</li>`, because the list is built from a
  // run of `<li>` elements, so the rule below already removes the
  // trailing one. It was covering a shape this renderer cannot emit.
  html = html.replace(/<\/li><br>/g, '</li>');

  // Sanitize with DOMPurify
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'code', 'pre', 'h2', 'h3', 'h4',
      'ul', 'ol', 'li', 'a', 'span', 'div', 'blockquote', 'hr',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'value', 'data-artifact-id', 'data-label', 'scope'],
  });
}

/**
 * Move block-level elements out of any enclosing `<p>`.
 *
 * The element set is the block-level elements this renderer's own passes
 * emit around text. The chart placeholder `<div>` is block-level too and is
 * not in it: a chart keeps the place and spacing it has always had.
 *
 * Each pattern is a non-greedy matched pair, which pairs the right tags
 * only while an element never holds its own kind. The passes keep to that:
 * lists are built flat from `<li>` runs, the table wrapper holds one table
 * and no inner `<div>`, a quote's lines are joined onto one line before any
 * other block pass runs, `<pre>` holds escaped text, and `<hr>` is void. A
 * general HTML transform could not assume any of that.
 */
function liftBlocksOutOfParagraphs(html: string): string {
  const BLOCK =
    '<pre>[\\s\\S]*?<\\/pre>' +
    '|<div class="table-wrap">[\\s\\S]*?<\\/div>' +
    '|<ul>[\\s\\S]*?<\\/ul>' +
    '|<ol>[\\s\\S]*?<\\/ol>' +
    '|<h[234]>[\\s\\S]*?<\\/h[234]>' +
    '|<blockquote>[\\s\\S]*?<\\/blockquote>' +
    '|<hr>';
  // `(?:(?!<\/p>)[\s\S])*?` keeps each side inside ONE paragraph —
  // a plain `[\s\S]*?` would happily span a `</p><p>` boundary and
  // splice two unrelated paragraphs together.
  const wrapped = new RegExp(
    `<p>((?:(?!<\\/p>)[\\s\\S])*?)(${BLOCK})((?:(?!<\\/p>)[\\s\\S])*?)<\\/p>`,
  );
  // A shell holding only line breaks or spaces is what the lift emptied;
  // it is dropped rather than left to render as a blank line.
  const emptied = (part: string): boolean =>
    part.replace(/<br>/g, '').trim().length === 0;
  // The breaks immediately either side of a lifted block are that
  // block's OWN separators — the newline that put it on its own line.
  // Left in place they render as a blank line above or below it, which
  // is the same stray gap this pass exists to remove. Only the touching
  // edges are trimmed; breaks anywhere else in the paragraph are the
  // author's and stay.
  const trimEdge = (part: string, edge: 'head' | 'tail'): string =>
    edge === 'head' ? part.replace(/(?:<br>)+$/, '') : part.replace(/^(?:<br>)+/, '');

  let out = html;
  // Loop: one paragraph can hold several blocks, and each pass lifts one.
  // Bounded by the fact that every pass strictly reduces the number of
  // block elements still inside a paragraph.
  for (let guard = 0; guard < 100; guard++) {
    const next = out.replace(wrapped, (_m, before: string, block: string, after: string) => {
      const head = emptied(before) ? '' : `<p>${trimEdge(before, 'head')}</p>`;
      const tail = emptied(after) ? '' : `<p>${trimEdge(after, 'tail')}</p>`;
      return `${head}${block}${tail}`;
    });
    if (next === out) break;
    out = next;
  }
  return out;
}

/**
 * Split a pipe-delimited table row into trimmed cell contents.
 *
 * Examples:
 *   splitRow("| a | b | c |")  → ["a", "b", "c"]
 *   splitRow("|:---|---:|---|") → [":---", "---:", "---"]
 *   splitRow("| h1 |  h2 ")    → ["h1", "h2"]   (trailing pipe optional)
 */
function splitRow(line: string): string[] {
  const parts = line.split('|');
  // Drop leading/trailing empties produced by outer pipes (when present).
  if (parts.length && parts[0].trim() === '') parts.shift();
  if (parts.length && parts[parts.length - 1].trim() === '') parts.pop();
  return parts.map((cell) => cell.trim());
}

/**
 * Build an HTML-attribute-safe value from a content-escaped header.
 *
 * The header text passed in has already gone through escapeHtml at the top
 * of renderMarkdown, so it may contain &quot; &lt; &gt; &amp; entity
 * references representing the user's original " < > & characters. When
 * those entity refs appear inside an HTML attribute value, the parser
 * decodes them back to raw chars during the attribute-value reading state.
 * That round-trip causes two problems:
 *   1. The decoded value may contain a substring DOMPurify treats as a
 *      sanitizer-bypass signature (most notably <script>...</script>),
 *      and the attribute is silently dropped.
 *   2. A literal " in the decoded value can confuse some downstream
 *      consumers even though HTML5 doesn't terminate the attribute on
 *      decoded "s.
 *
 * Mitigation:
 *   - Decode the four standard entity refs we emit, recovering raw text.
 *     `&amp;` is decoded LAST so `&amp;quot;` (literal `&quot;` in the
 *     user's input) doesn't accidentally turn into `"`.
 *   - Strip < and > entirely. The data-label is a CSS pseudo-element hint
 *     used in card-stacked layout; angle brackets in a column header are
 *     extremely rare and safely droppable for that presentation purpose.
 *   - Re-encode & and " for attribute context (the only two chars that
 *     need escaping inside a "-quoted attribute value).
 */
function attributeSafeLabel(escapedHeader: string): string {
  // Strip placeholder tokens — they reference content (inline code, code
  // blocks) that can't be rendered inside an HTML attribute value.
  // The visible cell still gets the full content; the data-label is just
  // a presentational hint for card-stacked layout.
  const raw = escapedHeader
    .replace(/\x00C\d+\x00/g, '')
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
  return raw
    .replace(/[<>]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;');
}

/**
 * Map a separator-row cell to an alignment class.
 *
 * Examples:
 *   alignFor("---")    → ""           (default left, no class)
 *   alignFor(":---")   → "ta-left"
 *   alignFor("---:")   → "ta-right"
 *   alignFor(":---:")  → "ta-center"
 */
function alignFor(sepCell: string): string {
  const trimmed = sepCell.trim();
  const left = trimmed.startsWith(':');
  const right = trimmed.endsWith(':');
  if (left && right) return 'ta-center';
  if (right) return 'ta-right';
  if (left) return 'ta-left';
  return '';
}

/** Escape HTML entities to prevent XSS before markdown processing. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ── Reveal segmentation ──────────────────────────────────────────────────
//
// Colocated with the grammar above ON PURPOSE. Every boundary below is read
// off a regex in `renderMarkdown`, not off CommonMark, because this renderer
// is narrower than the spec in ways that decide where a block ends:
//
//   - A blank line ENDS a list here. The wrapping pass joins items with
//     `\n?`, so a second newline breaks the run into two `<ul>`s. CommonMark
//     would keep a loose list open across it.
//   - A fence opens at a line starting ```. The fence regex opens one there
//     too, for any label without a backtick. It closes at the next ```
//     anywhere, because that is what the fence regex matches — not at a
//     line that is only a fence. One the answer never closes runs to its
//     end, once the answer has ended.
//   - A list item needs `- `, `* ` or `N. ` at column zero. No `+`, no
//     indentation: the item regexes are anchored and take no leading space.
//   - A thematic break is three or more of one of `-`, `*`, `_` and nothing
//     else but trailing space. Spaced marks (`- - -`) are not a rule.
//   - A quote line starts `> ` or is a lone `>`; a run of them is one
//     quote. `>text`, with no space, is not a quote.
//
// Widening the grammar above without widening these will cut a block the
// renderer would have joined, which is the one failure the reveal cannot
// absorb.

const FENCE_LINE = /^```/;
const HEADING_LINE = /^#{1,3} /;
const THEMATIC_BREAK_LINE = /^(?:-{3,}|\*{3,}|_{3,})[ \t]*$/;
const QUOTE_LINE = /^>(?: |$)/;
const LIST_ITEM_LINE = /^(?:[-*] |\d+\. )/;
const TABLE_ROW_LINE = /^\|/;
const TABLE_SEP_LINE = /^\|(?:[ \t]*:?-+:?[ \t]*\|)+[ \t]*$/;

/** A top-level block of answer source whose end is provable. */
export interface SourceBlock {
  /** Renderable block source: no leading or trailing blank lines.
   *
   *  Stripped because each released block gets its OWN container, so the
   *  separation between blocks is structural and does not need to survive
   *  in the text. Left in, the separator newlines would render as stray
   *  `<br>`s — the paragraph pass turns a lone `\n` into one — giving every
   *  container a blank line it did not ask for. Concatenating `text` across
   *  blocks therefore does NOT reproduce the source; `end` is what tiles it.
   *
   *  A released block is COMPLETE, so render it with `streaming = false`.
   *  Streaming mode exists to suppress a half-arrived table row, and it pays
   *  for that by requiring the final row to carry a trailing newline (T-37) —
   *  which is exactly the newline stripped here. */
  text: string;
  /** Offset just past the block, in the source it was cut from. */
  end: number;
}

interface SourceLine {
  text: string;
  /** Offset just past the newline, or the source end when unterminated. */
  end: number;
  terminated: boolean;
}

function scanLines(source: string): SourceLine[] {
  const lines: SourceLine[] = [];
  let at = 0;
  while (at < source.length) {
    const nl = source.indexOf('\n', at);
    if (nl === -1) {
      lines.push({ text: source.slice(at), end: source.length, terminated: false });
      break;
    }
    lines.push({ text: source.slice(at, nl), end: nl + 1, terminated: true });
    at = nl + 1;
  }
  return lines;
}

function isBlank(line: SourceLine | undefined): boolean {
  return line !== undefined && line.text.trim() === '';
}

function opensBlock(lines: SourceLine[], i: number): boolean {
  const text = lines[i].text;
  if (
    FENCE_LINE.test(text) ||
    HEADING_LINE.test(text) ||
    LIST_ITEM_LINE.test(text) ||
    THEMATIC_BREAK_LINE.test(text) ||
    QUOTE_LINE.test(text)
  ) {
    return true;
  }
  const next = lines[i + 1];
  return TABLE_ROW_LINE.test(text) && next !== undefined && TABLE_SEP_LINE.test(next.text);
}

/**
 * Cut accumulated answer source into top-level blocks whose ends are PROVABLE.
 *
 * Provability is the whole contract. A block comes back only when the source
 * already witnesses its end — a closing fence, or a terminated line beyond it —
 * so a caller that renders only what it receives cannot paint a half-built
 * construct. Whatever is not yet provable stays in the remainder past the last
 * block's `end`, and the same call repeated after more text arrives returns it.
 *
 * A list is ONE block, and so is a table and a fenced body: their items, rows
 * and lines are not top-level boundaries, so they are never cut apart.
 *
 * @param source accumulated answer text
 * @param total  the stream has ended and `source` will not grow, so the
 *               trailing block's end is proven by exhaustion
 */
export function segmentBlocks(source: string, total = false): SourceBlock[] {
  const lines = scanLines(source);
  const blocks: SourceBlock[] = [];
  let i = 0;
  let cut = 0;

  while (i < lines.length) {
    // Blank lines ride with the block that follows them, so released text
    // keeps the separation the paragraph pass reads.
    if (isBlank(lines[i])) {
      i++;
      continue;
    }

    const opener = lines[i].text;
    let endLine: number;
    let proven: boolean;

    if (FENCE_LINE.test(opener)) {
      let j = i + 1;
      while (j < lines.length && !lines[j].text.includes('```')) j++;
      if (j < lines.length) {
        endLine = j + 1; // the closing fence is its own witness
      } else if (total) {
        // Never closed, and the answer has ended: the fence runs to its end,
        // as the whole render draws it.
        endLine = lines.length;
      } else {
        // No closing fence yet. Nothing after it can be released either —
        // blocks are released in order — so the scan stops here entirely.
        break;
      }
      proven = true;
    } else {
      if (HEADING_LINE.test(opener) || THEMATIC_BREAK_LINE.test(opener)) {
        // One line each: a rule is drawn alone, as a heading is.
        endLine = i + 1;
      } else if (QUOTE_LINE.test(opener)) {
        // A quote is its run of `>` lines, never cut apart, as a list is.
        let j = i;
        while (j < lines.length && QUOTE_LINE.test(lines[j].text)) j++;
        endLine = j;
      } else if (
        TABLE_ROW_LINE.test(opener) &&
        lines[i + 1] !== undefined &&
        TABLE_SEP_LINE.test(lines[i + 1].text)
      ) {
        let j = i + 2;
        while (j < lines.length) {
          if (TABLE_ROW_LINE.test(lines[j].text)) {
            j++;
            continue;
          }
          // One blank line between rows is a streaming-chunk artifact the
          // table regex already absorbs (T-36); two of them end the table.
          const after = lines[j + 1];
          if (isBlank(lines[j]) && after !== undefined && TABLE_ROW_LINE.test(after.text)) {
            j += 2;
            continue;
          }
          break;
        }
        endLine = j;
      } else if (LIST_ITEM_LINE.test(opener)) {
        let j = i;
        while (j < lines.length && LIST_ITEM_LINE.test(lines[j].text)) j++;
        endLine = j;
      } else {
        let j = i + 1;
        while (j < lines.length && !isBlank(lines[j]) && !opensBlock(lines, j)) j++;
        endLine = j;
      }

      // Everything but a fence needs an outside witness: a TERMINATED line
      // past the block. An unterminated tail is still arriving and may extend
      // it — `- a\n- b\n-` is one list caught mid-keystroke, not a finished
      // list followed by a stray dash. Being late costs a chunk; being early
      // costs the invariant.
      const witness = lines[endLine];
      proven = total || (witness !== undefined && witness.terminated);
    }

    if (!proven) break;
    const stop = lines[endLine - 1].end;
    const raw = source.slice(cut, stop);
    blocks.push({
      text: raw.replace(/^(?:[ \t]*\n)+/, '').replace(/(?:\n[ \t]*)+$/, ''),
      end: stop,
    });
    cut = stop;
    i = endLine;
  }

  return blocks;
}
